import i18next, { type ResourceKey } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';

interface DetectionOptions {
  order?: string[];
  lookupCookie?: string;
  convertDetectedLanguage?: 'Iso15897' | ((lng: string) => string);
}

export const AVAILABLE_LOCALES = {
  'id-ID': 'Bahasa Indonesia',
  'ms-MY': 'Bahasa Melayu',
  'en-AU': 'English (Australia)',
  'en-CA': 'English (Canada)',
  'en-GB': 'English (United Kingdom)',
  'en-US': 'English (United States)',
  'es-MX': 'Español (México)',
  'fr-CA': 'Français (Canada)',
  'ja-JP': '日本語',
  'zh-HK': '繁體中文（香港）',
  'th-TH': 'ภาษาไทย',
  'vi-VN': 'Tiếng Việt',
} as const;

export type Locale = keyof typeof AVAILABLE_LOCALES;

/** Locale → namespace → translation resource tree (eager mega-bundle helper). */
export type BundledTranslations = Record<string, Record<string, ResourceKey>>;

/** Load one locale×namespace chunk (Vite dynamic import / import.meta.glob). */
export type TranslationChunkLoader = (
  lng: string,
  ns: string
) => Promise<ResourceKey | null | undefined>;

const DEFAULT_I18N_OPTIONS = {
  supportedLngs: Object.keys(AVAILABLE_LOCALES),
  fallbackLng: {
    default: ['en-US'],
  },
  ns: ['translation'],
  defaultNS: 'translation',
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: true,
  },
} as const;

const namespaceToModuleUrl = new Map<string, string>();

/**
 * True when the event-lander owns translation loading (chunked dynamic imports
 * or legacy eager register). Widgets skip per-widget locale glob storms.
 */
let landerManagedTranslationsActive = false;

export function isBundledTranslationsActive(): boolean {
  return landerManagedTranslationsActive;
}

/** Test-only: clear lander-managed flag and namespace registry. */
export function resetI18nRegistrationStateForTests(): void {
  landerManagedTranslationsActive = false;
  namespaceToModuleUrl.clear();
}

function collectNamespaces(bundled: BundledTranslations): string[] {
  const namespaces = new Set<string>();
  for (const nsMap of Object.values(bundled)) {
    for (const ns of Object.keys(nsMap)) {
      namespaces.add(ns);
    }
  }
  return [...namespaces];
}

/**
 * PD-2274 D9-C: init shared i18n with a Vite chunk loader (dynamic import /
 * import.meta.glob). Preloads critical namespaces only; other namespaces load
 * on demand via i18next-resources-to-backend (not HTTPBackend).
 */
export async function setupChunkedTranslations(options: {
  defaultNS: string;
  preloadNamespaces?: string[];
  loadChunk: TranslationChunkLoader;
  detectionOptions?: DetectionOptions;
  /** Optional fixed language (tests / non-browser). When set, skips detector order. */
  lng?: string;
}): Promise<typeof i18next> {
  const defaultNS = options.defaultNS;
  const preloadNamespaces = options.preloadNamespaces ?? [defaultNS];

  landerManagedTranslationsActive = true;

  for (const ns of preloadNamespaces) {
    namespaceToModuleUrl.set(ns, 'chunked-translations');
  }

  if (!i18next.isInitialized) {
    const backend = resourcesToBackend(async (lng: string, ns: string) => {
      const data = await options.loadChunk(lng, ns);
      return data ?? {};
    });

    // eslint-disable-next-line import/no-named-as-default-member
    let chain = i18next.use(initReactI18next).use(ICU).use(backend);

    if (!options.lng) {
      chain = chain.use(LanguageDetector);
    }

    await chain.init({
      ...DEFAULT_I18N_OPTIONS,
      ns: preloadNamespaces,
      defaultNS,
      // Allow namespaces beyond the preload set to load via the backend.
      partialBundledLanguages: true,
      ...(options.lng ? { lng: options.lng } : { detection: options.detectionOptions }),
    });
  }

  return i18next;
}

/**
 * Register a precompiled multi-namespace translations map into the shared
 * i18next singleton (eager). Prefer setupChunkedTranslations for event-lander
 * (D9-C). Still useful for tests and simple eager registration.
 */
export function registerBundledTranslations(
  bundled: BundledTranslations,
  options?: {
    defaultNS?: string;
    detectionOptions?: DetectionOptions;
  }
): typeof i18next {
  const namespaces = collectNamespaces(bundled);
  const defaultNS = options?.defaultNS ?? namespaces[0] ?? 'translation';

  landerManagedTranslationsActive = true;

  if (!i18next.isInitialized) {
    // eslint-disable-next-line import/no-named-as-default-member
    i18next
      .use(initReactI18next)
      .use(LanguageDetector)
      .use(ICU)
      .init({
        ...DEFAULT_I18N_OPTIONS,
        ns: namespaces.length > 0 ? namespaces : [defaultNS],
        defaultNS,
        detection: options?.detectionOptions,
      });
  }

  for (const [lng, nsMap] of Object.entries(bundled)) {
    for (const [namespace, data] of Object.entries(nsMap)) {
      namespaceToModuleUrl.set(namespace, 'bundled-translations');
      i18next.addResourceBundle(lng, namespace, data, true, true);
    }
  }

  return i18next;
}

export function setupWidgetI18n(
  moduleUrl: string,
  namespace: string,
  resources?: Record<string, ResourceKey>,
  detectionOptions?: DetectionOptions
): typeof i18next {
  if (namespaceToModuleUrl.has(namespace)) {
    // Already registered (e.g. via lander chunked / bundled translations) — reuse.
    if (resources) {
      for (const [lng, data] of Object.entries(resources)) {
        if (!i18next.hasResourceBundle(lng, namespace)) {
          i18next.addResourceBundle(lng, namespace, data, true, true);
        }
      }
    }
    return i18next;
  }

  namespaceToModuleUrl.set(namespace, moduleUrl);

  if (landerManagedTranslationsActive) {
    // Lander owns chunks: kick off on-demand load for this widget namespace.
    // react-i18next Suspense waits on loadNamespaces / backend reads.
    void i18next.loadNamespaces(namespace);
    if (resources) {
      for (const [lng, data] of Object.entries(resources)) {
        if (!i18next.hasResourceBundle(lng, namespace)) {
          i18next.addResourceBundle(lng, namespace, data, true, true);
        }
      }
    }
    return i18next;
  }

  if (!i18next.isInitialized) {
    // eslint-disable-next-line import/no-named-as-default-member
    i18next
      .use(initReactI18next)
      .use(LanguageDetector)
      .use(ICU)
      .init({
        ...DEFAULT_I18N_OPTIONS,
        // Restrict to the provided namespace to avoid stray loads
        ns: [namespace],
        defaultNS: namespace,
        detection: detectionOptions,
      });
  }

  // Add provided resources eagerly for this namespace
  if (resources) {
    for (const [lng, data] of Object.entries(resources)) {
      i18next.addResourceBundle(lng, namespace, data, true, true);
    }
  }

  return i18next;
}

export { useWidgetLocale } from './useWidgetLocale';

export default i18next;

function createLocaleImporters(
  globMap: Record<string, () => Promise<unknown>>
): Record<string, () => Promise<unknown>> {
  return Object.fromEntries(
    Object.entries(globMap).map(([path, loader]) => {
      const match = path.match(/\.\/i18n\/locales\/([^/]+)\/translation\.json$/);
      const locale = match ? match[1] : path;
      return [locale, loader as () => Promise<unknown>];
    })
  );
}

export async function loadLocaleResources(
  globMap: Record<string, () => Promise<unknown>>
): Promise<Record<string, ResourceKey>> {
  const importers = createLocaleImporters(globMap);
  const languages: string[] = [];
  const promises: Array<Promise<ResourceKey>> = [];

  for (const [lng, load] of Object.entries(importers)) {
    languages.push(lng);
    promises.push(
      load().then((mod) => (mod as { default?: ResourceKey })?.default ?? (mod as ResourceKey))
    );
  }

  const results = await Promise.all(promises);
  const resources: Record<string, ResourceKey> = {};
  for (const [i, lng] of languages.entries()) {
    const val = results[i]!;
    resources[lng] = val;
  }
  return resources;
}

/**
 * Load per-widget locale JSON only when not already provided by lander-managed
 * translations (chunked or eager). Skipping the `load()` calls prevents the
 * translation-file network storm on the event lander.
 */
export async function loadLocaleResourcesIfNeeded(
  namespace: string,
  globMap: Record<string, () => Promise<unknown>>
): Promise<Record<string, ResourceKey> | undefined> {
  if (landerManagedTranslationsActive || i18next.hasResourceBundle('en-US', namespace)) {
    return undefined;
  }
  return loadLocaleResources(globMap);
}
